import { useEffect } from "react";
import { motion } from "framer-motion";
import retirementAssetsIllustration from "../../../static/illustration--retirement-assets.svg";
import * as RadioGroup from "@radix-ui/react-radio-group";
import { useAnswer } from "../../../util/form";

export default function Confidence({
  advanceScreen,
  previousScreen,
  validation,
  updateValidation,
  value,
  screen,
}) {
  const { answer, setAnswer } = useAnswer(screen.id);

  useEffect(() => {
    if (value.length > 0) updateValidation(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    updateValidation(answer.length > 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [answer]);

  return (
    <motion.div
      className="w-full h-full"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.35 }}
    >
      <div className="px-6 py-16 text-center md:px-16 md:py-20">
        <div className="max-w-3xl mx-auto">
          <img
            className="block h-auto mx-auto w-28 md:w-36"
            src={retirementAssetsIllustration}
            alt=""
          />
          <h1 className="mt-6 mb-4 text-3xl md:mb-5 md:text-4xl md:mt-8">
            How confident do you usually feel about the long-term stability of
            the stock market?
          </h1>
          <div className="max-w-xl mx-auto">
            <p>
              This question is designed to assess your risk tolerance and
              investment preferences. Your answer will help us tailor your
              retirement plan to suit your goals and comfort level. There is no
              right or wrong answer, just be honest and realistic about how you
              view the stock market and its potential returns and losses over
              time.
            </p>
          </div>
        </div>
        <form className="mt-10 md:mt-14">
          <div className="max-w-[37.125rem] mx-auto">
            <h2 className="mb-4 text-xl font-light md:mb-6 md:text-2xl">
              Usually I feel confident about the long-term stability of the
              stock market:
            </h2>
            <RadioGroup.Root
              className="space-y-3"
              aria-label="Usually I feel confident about the long-term stability of the stock market:"
              defaultValue={answer}
            >
              <div className="flex items-center">
                <RadioGroup.Item
                  className="relative w-full rounded-lg border border-as-green-100 bg-transparent pb-4 pl-14 pr-4 pt-4 text-left before:absolute before:left-4 before:top-[1.1875rem] before:block before:h-4 before:w-4 before:rounded-full before:border before:border-as-green-100 before:bg-transparent before:content-[''] data-[state=checked]:bg-as-green-0 data-[state=checked]:before:bg-as-green-100"
                  value="stronglyAgree"
                  id="stronglyAgree"
                  onClick={() => setAnswer("stronglyAgree")}
                >
                  <RadioGroup.Indicator className="absolute left-[1.3125rem] top-6 h-1.5 w-1.5 rounded-full after:absolute after:inset-0 after:block after:h-1.5 after:w-1.5 after:rounded-full after:bg-white after:content-['']" />
                  <label
                    className="block text-sm font-bold leading-normal text-gray-800 cursor-pointer"
                    htmlFor="stronglyAgree"
                  >
                    Strongly Agree
                  </label>
                </RadioGroup.Item>
              </div>
              <div className="flex items-center">
                <RadioGroup.Item
                  className="relative w-full rounded-lg border border-as-green-100 bg-transparent pb-4 pl-14 pr-4 pt-4 text-left before:absolute before:left-4 before:top-[1.1875rem] before:block before:h-4 before:w-4 before:rounded-full before:border before:border-as-green-100 before:bg-transparent before:content-[''] data-[state=checked]:bg-as-green-0 data-[state=checked]:before:bg-as-green-100"
                  value="agree"
                  id="agree"
                  onClick={() => setAnswer("agree")}
                >
                  <RadioGroup.Indicator className="absolute left-[1.3125rem] top-6 h-1.5 w-1.5 rounded-full after:absolute after:inset-0 after:block after:h-1.5 after:w-1.5 after:rounded-full after:bg-white after:content-['']" />
                  <label
                    className="block text-sm font-bold leading-normal text-gray-800 cursor-pointer"
                    htmlFor="agree"
                  >
                    Agree
                  </label>
                </RadioGroup.Item>
              </div>
              <div className="flex items-center">
                <RadioGroup.Item
                  className="relative w-full rounded-lg border border-as-green-100 bg-transparent pb-4 pl-14 pr-4 pt-4 text-left before:absolute before:left-4 before:top-[1.1875rem] before:block before:h-4 before:w-4 before:rounded-full before:border before:border-as-green-100 before:bg-transparent before:content-[''] data-[state=checked]:bg-as-green-0 data-[state=checked]:before:bg-as-green-100"
                  value="neutral"
                  id="neutral"
                  onClick={() => setAnswer("neutral")}
                >
                  <RadioGroup.Indicator className="absolute left-[1.3125rem] top-6 h-1.5 w-1.5 rounded-full after:absolute after:inset-0 after:block after:h-1.5 after:w-1.5 after:rounded-full after:bg-white after:content-['']" />
                  <label
                    className="block text-sm font-bold leading-normal text-gray-800 cursor-pointer"
                    htmlFor="neutral"
                  >
                    Neutral
                  </label>
                </RadioGroup.Item>
              </div>
              <div className="flex items-center">
                <RadioGroup.Item
                  className="relative w-full rounded-lg border border-as-green-100 bg-transparent pb-4 pl-14 pr-4 pt-4 text-left before:absolute before:left-4 before:top-[1.1875rem] before:block before:h-4 before:w-4 before:rounded-full before:border before:border-as-green-100 before:bg-transparent before:content-[''] data-[state=checked]:bg-as-green-0 data-[state=checked]:before:bg-as-green-100"
                  value="disagree"
                  id="disagree"
                  onClick={() => setAnswer("disagree")}
                >
                  <RadioGroup.Indicator className="absolute left-[1.3125rem] top-6 h-1.5 w-1.5 rounded-full after:absolute after:inset-0 after:block after:h-1.5 after:w-1.5 after:rounded-full after:bg-white after:content-['']" />
                  <label
                    className="block text-sm font-bold leading-normal text-gray-800 cursor-pointer"
                    htmlFor="disagree"
                  >
                    Disagree
                  </label>
                </RadioGroup.Item>
              </div>
              <div className="flex items-center">
                <RadioGroup.Item
                  className="relative w-full rounded-lg border border-as-green-100 bg-transparent pb-4 pl-14 pr-4 pt-4 text-left before:absolute before:left-4 before:top-[1.1875rem] before:block before:h-4 before:w-4 before:rounded-full before:border before:border-as-green-100 before:bg-transparent before:content-[''] data-[state=checked]:bg-as-green-0 data-[state=checked]:before:bg-as-green-100"
                  value="stronglyDisagree"
                  id="stronglyDisagree"
                  onClick={() => setAnswer("stronglyDisagree")}
                >
                  <RadioGroup.Indicator className="absolute left-[1.3125rem] top-6 h-1.5 w-1.5 rounded-full after:absolute after:inset-0 after:block after:h-1.5 after:w-1.5 after:rounded-full after:bg-white after:content-['']" />
                  <label
                    className="block text-sm font-bold leading-normal text-gray-800 cursor-pointer"
                    htmlFor="stronglyDisagree"
                  >
                    Strongly Disagree
                  </label>
                </RadioGroup.Item>
              </div>
            </RadioGroup.Root>
            <div className="flex justify-center gap-3 mt-10 md:mt-14 md:gap-5">
              <button
                type="button"
                className="btn btn--secondary"
                onClick={previousScreen}
              >
                Back
              </button>
              <button
                type="button"
                className="btn btn--primary"
                disabled={Boolean(validation) === false}
                onClick={advanceScreen}
              >
                Continue
              </button>
            </div>
          </div>
        </form>
      </div>
    </motion.div>
  );
}
